import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    IconButton,
    Avatar,
    Divider,
    InputAdornment,
    FormHelperText,
} from '@mui/material';
import { useMsal } from "@azure/msal-react";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ContactsIcon from '@mui/icons-material/Contacts';
import SubjectIcon from '@mui/icons-material/Subject';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import InfoIcon from '@mui/icons-material/Info';

import { updateTicket } from '../../../reducers/kanban/ticketActions';
import { getAgentsFromApi } from '../../../actions/agentes';
import { useToken } from '../../../hooks/useToken';
import { client as axios } from '../../../axios/client';

// Estilos personalizados para el modal
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 12,
        minWidth: 500,
        maxWidth: 600,
    },
}));

// Constantes para los colores de prioridad
const PRIORITY_COLORS = {
    baja: { bg: '#4caf50', color: 'white' },
    media: { bg: '#ff9800', color: 'white' },
    alta: { bg: '#f44336', color: 'white' },
    default: { bg: '#e0e0e0', color: 'black' }
};

// Componente para mostrar la prioridad con colores
const PriorityChip = ({ priority }) => {
    const colors = PRIORITY_COLORS[priority] || PRIORITY_COLORS.default;

    return (
        <Chip
            label={priority.charAt(0).toUpperCase() + priority.slice(1)}
            sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 'bold',
                minWidth: 80,
            }}
        />
    );
};

// Componente para mostrar la información del contacto con diseño mejorado
const ContactInfo = ({ contact }) => {
    if (!contact) return null;

    // Obtener iniciales para el avatar
    const getInitials = () => {
        const firstName = contact.first_name || '';
        const lastName = contact.last_name || '';

        if (!firstName && !lastName) return '?';

        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    return (
        <Box sx={{
            mb: 3,
            p: 0,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.05)',
            background: 'linear-gradient(145deg, #f9f9f9 0%, #f3f8ff 100%)'
        }}>
            {/* Encabezado con título y avatar */}
            <Box sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                bgcolor: 'rgba(25, 118, 210, 0.04)'
            }}>
                <ContactsIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                <Typography variant="subtitle1" fontWeight="600" color="primary.main">
                    Información del Contacto
                </Typography>
            </Box>

            {/* Contenido principal con nombre y teléfono en columnas */}
            <Box sx={{ p: 2.5 }}>
                {/* Fila superior con nombre y teléfono en columnas */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2
                }}>
                    {/* Columna izquierda con avatar y nombre */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Avatar
                            sx={{
                                bgcolor: 'primary.main',
                                width: 50,
                                height: 50,
                                mr: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}
                        >
                            {getInitials()}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight="600" sx={{ lineHeight: 1.2 }}>
                                {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Sin nombre'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Cliente
                            </Typography>
                        </Box>
                    </Box>

                    {/* Columna derecha con teléfono */}
                    {contact?.phone && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            ml: 2,
                            minWidth: '180px'
                        }}>
                            <Avatar sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', width: 36, height: 36, mr: 1.5 }}>
                                <PhoneIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Teléfono
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {contact.phone}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Información adicional de contacto */}
                {contact?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', width: 36, height: 36, mr: 1.5 }}>
                            <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Email
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {contact.email}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// Estado inicial del formulario
const INITIAL_TICKET_STATE = {
    name: '',
    description: '',
    assigned_to: '',
    prioridad: 'media',
    metadata: {}
};

// Componente principal para crear tickets
const CreateTicketModal = ({ open, onClose, contact, boardType, boardId, onSuccess, isEditMode = false, ticketToEdit = null }) => {
    // Hooks
    const dispatch = useDispatch();
    const isMounted = useRef(true);
    const { getTokenSilent } = useToken();
    const { accounts } = useMsal();

    // Estados de Redux
    const { agent, error: reduxError } = useSelector(state => ({
        agent: state.agent,
        error: state.kanban.error
    }));

    const { agents } = useSelector(state => state.agentes);
    const { columns: { items: columns = [] } = { items: [] } } = useSelector(state => state.kanban);

    // Obtener el agente actual de diferentes maneras usando useMemo para optimización
    const currentAgent = React.useMemo(() => {
        return agent?.currentAgent || agent?.profile || agent || {};
    }, [agent]);

    // ID del agente actual logueado (priorizando MSAL, fuente confiable)
    const currentAgentId = React.useMemo(() => {
        const userProfile = accounts[0] || {};
        return userProfile?.localAccountId ||
            agent?.profile?.sub ||
            currentAgent?.profile?.sub ||
            agent?.id ||
            currentAgent?.id ||
            agent?._id ||
            currentAgent?._id ||
            '';
    }, [accounts, agent, currentAgent]);


    useEffect(() => {
            // Debug logs eliminados
    }, [agent, currentAgent]);

    // Referencias
    const hasLoadedColumns = useRef(false);

    const agentIdRef = useRef(null);

    // UI States
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [formError, setFormError] = useState('');
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Efecto para la animación de progreso
    useEffect(() => {
        let progressInterval;
        if (isSubmitting) {
            progressInterval = setInterval(() => {
                setLoadingProgress((oldProgress) => {
                    if (oldProgress >= 90) {
                        return oldProgress;
                    }
                    return oldProgress + 10;
                });
            }, 500);
        } else {
            setLoadingProgress(0);
        }

        return () => {
            if (progressInterval) {
                clearInterval(progressInterval);
            }
        };
    }, [isSubmitting]);

    // Estados del formulario
    const [ticketData, setTicketData] = useState(INITIAL_TICKET_STATE);
    const [metadataFields, setMetadataFields] = useState([]);
    const [newMetadataKey, setNewMetadataKey] = useState('');
    const [newMetadataValue, setNewMetadataValue] = useState('');
    const [showAddMetadataField, setShowAddMetadataField] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [availableAgents, setAvailableAgents] = useState([]);

    // Estado para controlar si el botón está deshabilitado.
    // En edición la asignación es opcional: cualquier agente puede editar
    // el ticket aunque no tenga agente asignado.
    const isButtonDisabled = !ticketData?.name?.trim() ||
        !ticketData?.description?.trim() ||
        (!isEditMode && !ticketData?.assigned_to) ||
        isSubmitting;

    // Constantes derivadas
    const normalizedBoardType = boardType === 'support' ? 'soporte' : boardType;
    const finalContact = ticketToEdit ? { ...ticketToEdit.contact, id_customer: ticketToEdit.id_customer } : contact;


    // Handle Redux errors
    useEffect(() => {
        if (reduxError) {
            setFormError(reduxError);
        }
    }, [reduxError]);

    // Close error notification
    const handleCloseError = () => {
        setFormError('');
    };

    // Resetear el formulario cuando se abre/cierra el modal
    useEffect(() => {
        if (open) {
            if (isEditMode && ticketToEdit) {
                // Rellenar el formulario para edición
                const metadata = ticketToEdit.meta_data ?
                    (typeof ticketToEdit.meta_data === 'string' ? JSON.parse(ticketToEdit.meta_data) : ticketToEdit.meta_data)
                    : {};

                setTicketData({
                    name: ticketToEdit.name || '',
                    description: ticketToEdit.description || '',
                    assigned_to: ticketToEdit.assigned_to_agent || currentAgentId,
                    prioridad: metadata.prioridad || 'media',
                    metadata: {}
                });

                const additionalMetadata = Object.entries(metadata)
                    .filter(([key]) => !['prioridad', 'description'].includes(key))
                    .map(([key, value]) => ({ key, value: String(value) }));

                setMetadataFields(additionalMetadata);
            } else {
                // Resetear para creación
                resetForm();
            }
        }

        return () => {
            if (!open) {
                hasLoadedColumns.current = false;
                handleCloseError();
            }
        };
    }, [open, isEditMode, ticketToEdit, boardId, currentAgentId]);

    // Función para resetear el formulario
    const resetForm = () => {
        agentIdRef.current = null;
        setTicketData(INITIAL_TICKET_STATE);
        setMetadataFields([]);
        setNewMetadataKey('');
        setNewMetadataValue('');
        setShowAddMetadataField(false);
        setSubmitError(null);
        setSuccessMessage('');
        setShowSuccess(false);
    };

    // Efecto para actualizar las columnas disponibles
    useEffect(() => {
        if (!columns?.length) {
            return;
        }

        const columnsForThisBoard = columns.filter(col =>
            String(col.board_id) === String(boardId)
        );

        if (columnsForThisBoard.length > 0) {
            const targetColumn = columnsForThisBoard.find(
                col => parseInt(col.position, 10) === 1
            ) || columnsForThisBoard[0];

            setSelectedColumn(targetColumn);
        } else {
            setSubmitError('No hay columnas disponibles para este tablero. Por favor, contacta al administrador.');
            setSelectedColumn(null);
        }
    }, [columns, boardId]);

    // Cargar columnas cuando se abre el modal
    useEffect(() => {
        if (!open || !boardId || hasLoadedColumns.current) {
            return;
        }

        hasLoadedColumns.current = true;

        // No cargar columnas, ya están disponibles en el state de Redux
        // Las columnas ya fueron cargadas cuando el usuario entró al tablero
        
    }, [open, boardId, dispatch, getTokenSilent, normalizedBoardType]);

    // Resetear el flag de columnas cargadas cuando se cierra el modal
    useEffect(() => {
        if (!open) {
            hasLoadedColumns.current = false;
        }
    }, [open]);

    // Cargar agentes cuando se abre el modal
    useEffect(() => {
        let isMounted = true;
        let loadingTimeout;

        const loadAgents = async () => {
            if (!open) return;

            try {
                loadingTimeout = setTimeout(() => {
                    if (isMounted) setLoading(true);
                }, 100);

                const token = await getTokenSilent();
                if (!token) throw new Error('No se pudo obtener el token de autenticación');

                const customerId = finalContact?.id_customer || currentAgent?.id_customer;
                if (!customerId) {
                    throw new Error('No se pudo determinar el cliente');
                }

                const hasAgents = agents?.items?.some(agent => agent.id_customer === customerId);
                if (!hasAgents) {
                    await dispatch(getAgentsFromApi(customerId, token));
                }

                if (isMounted) {
                    setFormError('');
                }

            } catch (error) {
                if (isMounted) {
                    setFormError('Error al cargar la lista de agentes. Intente nuevamente.');
                }
            } finally {
                clearTimeout(loadingTimeout);
                if (isMounted) setLoading(false);
            }
        };

        if (open) loadAgents();

        return () => {
            isMounted = false;
            clearTimeout(loadingTimeout);
        };
    }, [open, dispatch, getTokenSilent, finalContact?.id_customer, currentAgent?.id_customer, agents?.items]);

    // Actualizar agentes disponibles cuando cambia la lista de agentes
    useEffect(() => {
        if (!agents?.items) return;

        // En modo edición, asegurarse de que el agente asignado al ticket
        // aparezca en el Select aunque no pertenezca a la lista del cliente
        // actual (p.ej. tickets creados por otro agente u otro cliente).
        const assignedId = String(ticketToEdit?.assigned_to_agent || currentAgentId || '');
        let options = agents.items;
        if (isEditMode && assignedId && !agents.items.some(a => String(a._id) === assignedId)) {
            const assignedName = ticketToEdit?.assigned_agent_name || 'Agente';
            options = [
                { _id: assignedId, display_name: assignedName, email: assignedName },
                ...agents.items
            ];
        }

        setAvailableAgents(options);

        // Limpiar error de selección de agente si hay agentes disponibles
        if (submitError?.includes('seleccione un agente') && options.length > 0) {
            setSubmitError(null);
        }
    }, [agents, submitError, isEditMode, ticketToEdit, currentAgentId]);

    // Manejador de cambio en los campos del formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setTicketData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Generar metadatos para el ticket
    const generateMetadata = () => {
        const baseMetadata = { prioridad: ticketData.prioridad };
        const additionalMetadata = {};

        metadataFields.forEach(field => {
            additionalMetadata[field.key] = field.value;
        });

        return JSON.stringify({
            ...baseMetadata,
            ...additionalMetadata
        });
    };

    // Manejadores de metadatos
    const handleAddMetadataField = () => {
        if (!newMetadataKey.trim() || !newMetadataValue.trim()) return;

        setMetadataFields(prev => [
            ...prev,
            { key: newMetadataKey.trim(), value: newMetadataValue.trim() }
        ]);
        setNewMetadataKey('');
        setNewMetadataValue('');
        setShowAddMetadataField(false);
    };

    const handleRemoveMetadataField = (index) => {
        setMetadataFields(prev => prev.filter((_, i) => i !== index));
    };

    // Manejadores de UI
    const handleCloseSuccess = () => {
        setShowSuccess(false);
    };

    const forceCloseModal = (eventOrData = null) => {
        // Si el parámetro es un evento (tiene propiedades como type, target, etc.), 
        // significa que se cerró el modal sin crear un ticket
        const isEvent = eventOrData && (
            eventOrData.type || 
            eventOrData.target || 
            eventOrData._reactName ||
            (typeof eventOrData === 'object' && eventOrData.nativeEvent)
        );
        
        const ticketData = isEvent ? null : eventOrData;
        
        resetForm();
        setTimeout(() => onClose(ticketData), 100);
    };

    // Validaciones
    const validateForm = () => {

        if (!ticketData.name.trim()) {
            setFormError('Debe ingresar un nombre para el ticket');
            return false;
        }

        if (!ticketData.description.trim()) {
            setFormError('Debe ingresar una descripción para el ticket');
            return false;
        }

        // En edición la asignación es opcional (no se obliga a reasignar)
        if (!isEditMode && !ticketData.assigned_to) {
            setFormError('Debe seleccionar un agente para el ticket');
            return false;
        }

        // En edición no se cambia la columna, por lo que no se exige seleccionarla
        if (!isEditMode && !selectedColumn) {
            setFormError('No se ha podido determinar la columna de destino');
            return false;
        }
        return true;
    };

    // Envío del formulario
    const handleSubmit = async (e) => {
        e?.preventDefault();

        // Validar formulario
        if (!validateForm()) {
            // Mostrar el error ya establecido en validateForm()
            return;
        }

        if (isEditMode) {
            await handleUpdate();
        } else {
            await handleCreate();
        }
    };

    // Manejador para actualizar un ticket existente
    const handleUpdate = async () => {
        try {
            setIsSubmitting(true);
            setLoading(true);
            setFormError('');

            const token = await getTokenSilent();
            if (!token) throw new Error('No se pudo obtener el token de autenticación');

            // Obtener el nombre del agente que realiza el cambio
            const changedByAgentName = agent?.profile?.preferred_username ||
                currentAgent?.preferred_username ||
                agent?.email?.split('@')[0] ||
                currentAgent?.email?.split('@')[0] ||
                'Agente';

            // Obtener el ID del agente que realiza el cambio (forma robusta)
            const userProfile = accounts[0] || {};
            const changedByAgentId = userProfile?.localAccountId || agent?.profile?.sub || currentAgent?.profile?.sub || agent?.id || currentAgent?.id || agent?._id;

            if (!changedByAgentId) {
                throw new Error("No se pudo identificar al agente actual para registrar el cambio.");
            }

            // Buscar el agente asignado y formatear su nombre.
            // Si el agente no está en la lista local (ticket de otro agente/cliente),
            // preservar el nombre que ya trae el ticket en lugar de degradar a 'Agente'.
            const assignedAgent = agents?.items?.find(a => String(a._id) === String(ticketData.assigned_to));
            const assignedAgentName = assignedAgent?.email?.split('@')[0] ||
                assignedAgent?.display_name ||
                ticketToEdit?.assigned_agent_name ||
                'Agente';

            // En edición, si no se tocó la asignación, conservar la del ticket original
            const finalAssignedTo = ticketData.assigned_to || ticketToEdit?.assigned_to_agent || null;

            const updateData = {
                name: ticketData.name,
                description: ticketData.description,
                assigned_to_agent: finalAssignedTo,
                assigned_agent_name: assignedAgentName,
                meta_data: generateMetadata(),
                changed_by_agent_id: changedByAgentId,
                changed_by_agent_name: changedByAgentName.toLowerCase().replace(/\s+/g, '.')
            };

            const updatedTicket = await dispatch(updateTicket(ticketToEdit.id, updateData, token));

            if (isMounted.current) {
                // Al ser exitoso, llamamos a onSuccess para que el componente padre
                // maneje el cierre de ambos modales y muestre la notificación.
                if (onSuccess) {
                    onSuccess(updatedTicket);
                } else {
                    onClose(updatedTicket); // Mantener el comportamiento original como fallback
                }
            }
        } catch (error) {
            if (isMounted.current) {
                setFormError(error.message || 'Error al actualizar el ticket');
            }
        } finally {
            if (isMounted.current) {
                setIsSubmitting(false);
                setLoading(false);
            }
        }
    };


    // Envío del formulario
    const handleCreate = async () => {


        try {
            // Iniciar animación de carga
            setIsSubmitting(true);
            setLoading(true);
            setLoadingProgress(10);
            setFormError('');

            // Obtener el token
            let token;
            try {
                token = await getTokenSilent();
                if (!token) {
                    throw new Error('No se pudo obtener el token de autenticación');
                }
            } catch (tokenError) {
                throw new Error('Error de autenticación');
            }

            setLoadingProgress(50);

            if (!selectedColumn) {
                throw new Error('Por favor, selecciona una columna');
            }

            // Funciones auxiliares para normalizar datos
            const getAgentId = (agent) => {
                if (!agent) return '';
                // Intentar obtener el ID de diferentes maneras
                return agent.id || agent._id ||
                    (agent.tokenParsed && agent.tokenParsed.sub) ||
                    (agent.profile && (agent.profile.sub || agent.profile.id || agent.profile._id)) || '';
            };

            const getContactName = (contact) => {
                if (contact.name) return contact.name;
                const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                return fullName || String(contact.phone) || 'Contacto';
            };

            const getContactId = (contact) => {
                return contact.id || contact._id;
            };

            // Obtener el nombre de usuario completo (jose.rodriguez)
            const currentAgentName = agent?.profile?.preferred_username ||
                currentAgent?.preferred_username ||
                agent?.email?.split('@')[0] ||
                currentAgent?.email?.split('@')[0] ||
                currentAgent?.display_name ||
                currentAgent?.name ||
                'Agente';

            // Si el nombre contiene espacios, reemplazamos por punto
            const formattedAgentName = currentAgentName.includes(' ') ?
                currentAgentName.toLowerCase().replace(/\s+/g, '.') :
                currentAgentName.toLowerCase();

            // Solo calcular agentId si no está cacheado
            if (!agentIdRef.current) {
                agentIdRef.current = getAgentId(currentAgent) ||
                    getAgentId(agent) ||
                    (agent?.profile?.sub) ||
                    (agent?.tokenParsed?.sub);
            }
            
            const agentId = agentIdRef.current;

            if (!agentId) {
                console.error('No se pudo obtener el ID del agente. Estado completo:', {
                    currentAgent,
                    agentState: agent,
                    availableKeys: agent ? Object.keys(agent) : []
                });
                throw new Error('No se pudo identificar al agente actual. Por favor, inicie sesión nuevamente.');
            }

            // Buscar el agente asignado y formatear su nombre
            const assignedAgent = agents?.items?.find(a => a._id === ticketData.assigned_to);

            // Obtener el nombre del agente asignado con formato consistente
            let assignedAgentName = assignedAgent?.email?.split('@')[0] ||
                assignedAgent?.preferred_username ||
                assignedAgent?.display_name ||
                assignedAgent?.name ||
                currentAgentName;

            // Si el nombre contiene espacios, reemplazar por puntos
            if (assignedAgentName && assignedAgentName.includes(' ')) {
                assignedAgentName = assignedAgentName.toLowerCase().replace(/\s+/g, '.');
            } else if (assignedAgentName) {
                assignedAgentName = assignedAgentName.toLowerCase();
            }
            const contactId = getContactId(contact);
            const contactName = getContactName(contact);

            // Datos unificados - una sola preparación
            const ticketDataToSend = {
                name: ticketData.name,
                description: ticketData.description,
                column_id: selectedColumn.id,
                board_id: boardId,
                created_by_agent: agentId,
                creator_agent_name: formattedAgentName,
                id_customer: finalContact?.id_customer || currentAgent?.id_customer,
                id_contact: contactId,
                name_contact: contactName,
                assigned_to_agent: ticketData.assigned_to,
                assigned_agent_name: assignedAgentName,
                meta_data: generateMetadata()
            };

            try {
                const headers = {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                };

                const params = { ...ticketDataToSend };

                // Eliminar explícitamente status y position si existen
                delete params.status;
                delete params.position;

                // URL del endpoint
                const url = `/kanban/columns/${ticketDataToSend.column_id}/tickets`;

                // Realizar la petición POST enviando los datos como parámetros de consulta.
                // Se espera la respuesta 2xx antes de mostrar éxito: el modal no se cierra
                // hasta confirmar que el backend realmente creó el ticket.
                const response = await axios.post(
                    url,
                    null,
                    {
                        headers,
                        params,
                        timeout: 15000
                    }
                );

                // Crear el objeto del ticket con la respuesta
                const createdTicket = {
                    ...response.data,
                    name: response.data.name || ticketDataToSend.name,
                    description: response.data.description || ticketDataToSend.description,
                    column_id: ticketDataToSend.column_id
                };

                // Actualizar el estado de Redux
                dispatch({
                    type: 'CREATE_TICKET_SUCCESS',
                    payload: createdTicket
                });

                // Establecer mensaje de éxito solo si el componente sigue montado
                if (isMounted.current) {
                    setSuccessMessage(`Ticket "${createdTicket.name}" creado correctamente`);
                    setShowSuccess(true);
                    setLoadingProgress(100);

                    // Preparar la información del ticket para devolver al componente padre
                    const ticketInfo = {
                        ...createdTicket,
                        name: ticketData.name,
                        board_name: normalizedBoardType === 'soporte' ? 'Soporte' : 'General',
                        board_id: boardId,
                        column_name: selectedColumn?.name || 'Primera columna'
                    };

                    // Llamar al callback de éxito si existe
                    if (onSuccess) {
                        onSuccess(ticketInfo);
                    }

                    // Notificar al componente padre que el ticket fue creado exitosamente
                    // El componente padre se encargará de cerrar este modal y abrir el TicketActionModal
                    onClose(ticketInfo);
                }
            } catch (createError) {
                console.error('Error al crear el ticket:', createError);
                if (createError.response?.data) {
                    console.error('Detalles del error:', createError.response.data);
                    throw new Error(createError.response.data.message || createError.message);
                }
                throw createError;
            }
        } catch (submitError) {
            if (isMounted.current) {
                setFormError(submitError.message || 'Error al crear el ticket');
            }
        } finally {
            if (isMounted.current) {
                setIsSubmitting(false);
                setLoading(false);
            }
        }
    };

    // Condición para habilitar el botón de crear
    return (
        <>
            <StyledDialog open={open} onClose={() => forceCloseModal(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#f8f9fa'
                }}>
                    <Typography variant="h6" component="div" fontWeight="500">
                        {isEditMode ? 'Editar Ticket' : 'Crear Nuevo Ticket'}
                    </Typography>
                </DialogTitle>

                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{ pt: 3 }}>
                        {/* Mensaje de error */}
                        {(formError || reduxError || submitError) && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {String(submitError || formError || reduxError || 'Error desconocido')}
                            </Alert>
                        )}

                        {/* Información del contacto */}
                        <ContactInfo contact={finalContact} />

                        {/* Selector de prioridad con diseño mejorado */}
                        <Box sx={{
                            mb: 3,
                            p: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f9fafc 100%)'
                        }}>
                            <Box sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                bgcolor: 'rgba(25, 118, 210, 0.04)'
                            }}>
                                <PriorityHighIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight="500" color="primary.main">
                                    Prioridad del Ticket
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2 }}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="prioridad-label">Seleccionar Prioridad</InputLabel>
                                    <Select
                                        labelId="prioridad-label"
                                        name="prioridad"
                                        value={ticketData.prioridad}
                                        onChange={handleChange}
                                        label="Seleccionar Prioridad"
                                        disabled={loading}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <Box sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    bgcolor: ticketData.prioridad === 'baja' ? 'success.light' :
                                                        ticketData.prioridad === 'media',
                                                    mr: 1
                                                }} />
                                            </InputAdornment>
                                        }
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PriorityChip priority={selected} />
                                            </Box>
                                        )}
                                    >
                                        <MenuItem value="baja">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PriorityChip priority="baja" />
                                                <Typography>Baja</Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="media">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PriorityChip priority="media" />
                                                <Typography>Media</Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="alta">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PriorityChip priority="alta" />
                                                <Typography>Alta</Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Nombre del ticket con diseño mejorado */}
                        <Box sx={{
                            mb: 3,
                            p: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f9fafc 100%)'
                        }}>
                            <Box sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                bgcolor: 'rgba(25, 118, 210, 0.04)'
                            }}>
                                <SubjectIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight="500" color="primary.main">
                                    Nombre del Ticket
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2 }}>
                                <TextField
                                    autoFocus
                                    name="name"
                                    placeholder="Ingrese un título descriptivo"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={ticketData.name}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AssignmentIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Descripción/Motivo del ticket con diseño mejorado */}
                        <Box sx={{
                            mb: 3,
                            p: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f9fafc 100%)'
                        }}>
                            <Box sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                bgcolor: 'rgba(25, 118, 210, 0.04)'
                            }}>
                                <AssignmentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight="500" color="primary.main">
                                    Descripción del Ticket
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2 }}>
                                <TextField
                                    name="description"
                                    placeholder="Describa el motivo o problema a resolver"
                                    type="text"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    value={ticketData.description}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    inputProps={{ maxLength: 255 }}
                                />
                            </Box>
                        </Box>

                        {/* Metadatos adicionales (opcional) - Movido debajo de la descripción */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{
                                p: 0,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                border: '1px solid rgba(0,0,0,0.08)',
                                background: 'linear-gradient(145deg, #ffffff 0%, #f9fafc 100%)'
                            }}>
                                <Box sx={{
                                    p: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                                    bgcolor: 'rgba(25, 118, 210, 0.04)'
                                }}>
                                    <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                    <Typography variant="subtitle2" fontWeight="500" color="primary.main">
                                        Información adicional (opcional)
                                    </Typography>
                                </Box>

                                <Box sx={{ p: 2 }}>
                                    {/* Mostrar los metadatos ya agregados como etiquetas */}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {metadataFields.length > 0 ? (
                                            metadataFields.map((field, index) => (
                                                <Chip
                                                    key={index}
                                                    label={`${field.key}: ${field.value}`}
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    onDelete={() => handleRemoveMetadataField(index)}
                                                    deleteIcon={<DeleteIcon fontSize="small" />}
                                                    sx={{ bgcolor: '#f5f5f5' }}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No hay información adicional
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Formulario para agregar nuevos metadatos */}
                                    {showAddMetadataField ? (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                            <TextField
                                                size="small"
                                                label="Clave"
                                                value={newMetadataKey}
                                                onChange={(e) => setNewMetadataKey(e.target.value)}
                                                disabled={loading}
                                                sx={{ flexGrow: 1 }}
                                            />
                                            <TextField
                                                size="small"
                                                label="Valor"
                                                value={newMetadataValue}
                                                onChange={(e) => setNewMetadataValue(e.target.value)}
                                                disabled={loading}
                                                sx={{ flexGrow: 1 }}
                                            />
                                            <IconButton
                                                color="primary"
                                                onClick={handleAddMetadataField}
                                                disabled={!newMetadataKey.trim() || !newMetadataValue.trim() || loading}
                                                sx={{ mt: 1 }}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button
                                            startIcon={<AddIcon />}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setShowAddMetadataField(true)}
                                            disabled={loading}
                                        >
                                            Agregar campo
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Selector de agente asignado con diseño mejorado */}
                        <Box sx={{
                            mb: 3,
                            p: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f9fafc 100%)'
                        }}>
                            <Box sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                bgcolor: 'rgba(25, 118, 210, 0.04)'
                            }}>
                                <AssignmentIndIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight="500" color="primary.main">
                                    Asignación del Ticket
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2 }}>
                                <FormControl fullWidth variant="outlined" error={submitError && submitError.includes('seleccione un agente')}>
                                    <InputLabel id="assigned-to-label">Asignar a</InputLabel>
                                    <Select
                                        labelId="assigned-to-label"
                                        name="assigned_to"
                                        value={ticketData.assigned_to}
                                        onChange={handleChange}
                                        label="Asignar a"
                                        disabled={loading}
                                        required={!isEditMode}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <PersonIcon color={ticketData.assigned_to ? "primary" : "action"} />
                                            </InputAdornment>
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Seleccione un agente</em>
                                        </MenuItem>
                                        {availableAgents.map(agent => (
                                            <MenuItem key={agent._id} value={agent._id}>
                                                <Typography variant="body2">
                                                    {agent.email ? agent.email.split('@')[0] : 'Agente'}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {submitError && submitError.includes('seleccione un agente') ?
                                            'Debe seleccionar un agente para crear el ticket' :
                                            'Seleccione un agente para asignar el ticket'}
                                    </FormHelperText>
                                </FormControl>
                            </Box>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                        <Button
                            onClick={forceCloseModal}
                            color="inherit"
                            disabled={isSubmitting}
                            sx={{ mr: 1 }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isButtonDisabled || isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}

                        >
                            {isSubmitting ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    <Typography variant="body2">
                                        {isEditMode ? 'Guardando...' : `Creando ticket... ${loadingProgress}%`}
                                    </Typography>
                                </Box>
                            ) : (isEditMode ? 'Guardar Cambios' : 'Crear Ticket')}
                        </Button>
                    </DialogActions>
                </form>
            </StyledDialog>

            {/* Notificación de éxito */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={2000}
                onClose={handleCloseSuccess}
                message={successMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

export default CreateTicketModal;